import { readFile, BaseDirectory, exists, remove } from '@tauri-apps/plugin-fs';
import { join, appDataDir } from '@tauri-apps/api/path';
import { convertFileSrc } from '@tauri-apps/api/core';
import { fetch as pluginFetch } from '@tauri-apps/plugin-http';

import {
  saveCollection,
  retrieveCollection,
  reconstructResponse,
  enqueuePendingChange,
  upsertItem,
  upsertAppStateBatch,
  deleteItem,
} from '../utils/dbUtils';
import { processPendingChanges } from '../utils/syncQueue';

import type {
  FetchOptions,
  GetAllFetchOptions,
  GetAllRequestBody,
  DownloadOptions,
  DownloadProgress,
  DownloadResult,
} from './types';

// Add request counter to detect infinite loops
let requestCount = 0;

// ============================================================================
// Proxy-aware fetch (Queensland EQ schools)
// ============================================================================
// On AD-joined Windows machines behind an authenticated proxy (the EQ network
// pattern), pure-Rust HTTP clients fail because they can't speak SSPI. The
// Rust side detects this at startup and exposes `app_fetch`, which goes through
// WinHTTP with autologon — i.e. the OS does the NTLM/Negotiate handshake using
// the logged-in user's credentials, no prompt.
//
// On every other configuration (macOS, mobile, Windows without a system proxy),
// we keep using `@tauri-apps/plugin-http`'s fetch unchanged.

let _useProxyFetch: boolean | null = null;

async function shouldUseProxyFetch(): Promise<boolean> {
  if (_useProxyFetch !== null) return _useProxyFetch;
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    _useProxyFetch = await invoke<boolean>('should_use_proxy_fetch');
    if (_useProxyFetch) {
      console.log('[fetch-wrapper] System proxy detected — routing requests via WinHTTP');
    }
  } catch {
    _useProxyFetch = false;
  }
  return _useProxyFetch;
}

function bytesToBase64(bytes: Uint8Array): string {
  // String.fromCharCode chokes on huge arrays; chunk to keep it stable for
  // request bodies up to a few MB (typical API + small uploads).
  const CHUNK = 0x8000;
  let s = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(s);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function bodyToBase64(body: BodyInit | null | undefined): Promise<string | null> {
  if (body === undefined || body === null) return null;
  if (typeof body === 'string') {
    return bytesToBase64(new TextEncoder().encode(body));
  }
  if (body instanceof ArrayBuffer) {
    return bytesToBase64(new Uint8Array(body));
  }
  if (ArrayBuffer.isView(body)) {
    return bytesToBase64(new Uint8Array(body.buffer, body.byteOffset, body.byteLength));
  }
  if (body instanceof Blob) {
    return bytesToBase64(new Uint8Array(await body.arrayBuffer()));
  }
  // FormData / URLSearchParams / ReadableStream — let the fallback path handle it.
  throw new Error('Unsupported body type for proxy-fetch path');
}

function headersToPairs(headers: HeadersInit | undefined): [string, string][] {
  if (!headers) return [];
  if (headers instanceof Headers) {
    const out: [string, string][] = [];
    headers.forEach((v, k) => out.push([k, v]));
    return out;
  }
  if (Array.isArray(headers)) return headers as [string, string][];
  return Object.entries(headers).map(([k, v]) => [k, String(v)] as [string, string]);
}

async function proxyFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const { invoke } = await import('@tauri-apps/api/core');
  const body_b64 = await bodyToBase64(options.body);
  const result = await invoke<{
    status: number;
    headers: [string, string][];
    body_b64: string;
  }>('app_fetch', {
    request: {
      url,
      method: (options.method || 'GET').toUpperCase(),
      headers: headersToPairs(options.headers),
      body_b64,
    },
  });
  const responseHeaders = new Headers();
  for (const [k, v] of result.headers) {
    try { responseHeaders.append(k, v); } catch { /* skip illegal header names */ }
  }
  // Cast: TS 5.7's tightened ArrayBufferLike → ArrayBuffer split makes the
  // direct conversion fail at the type level, but the runtime value is a
  // freshly allocated Uint8Array with a non-shared backing buffer.
  const bytes = base64ToBytes(result.body_b64);
  return new Response(bytes as unknown as BodyInit, {
    status: result.status,
    headers: responseHeaders,
  });
}

async function adaptiveFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const useProxy = await shouldUseProxyFetch();
  // FormData / URLSearchParams / streams aren't supported in the WinHTTP path
  // yet — fall back to plugin-http for those (rare; used for uploads).
  const incompatible =
    options.body instanceof FormData ||
    (typeof URLSearchParams !== 'undefined' && options.body instanceof URLSearchParams) ||
    (typeof ReadableStream !== 'undefined' && options.body instanceof ReadableStream);
  if (useProxy && !incompatible) {
    return proxyFetch(url, options);
  }
  return pluginFetch(url, options);
}

// ============================================================================
// Online/Offline State Management
// ============================================================================

// Global online state - true by default, updated based on actual API responses
let _isOnline = true;
let _oidcAuthorityUrl: string | null = null;
let _onlineStateListeners: Set<(online: boolean) => void> = new Set();
let _verifyingOffline = false;
let _lastVerificationTime = 0;
const VERIFICATION_THROTTLE_MS = 5000; // Only verify once every 5 seconds

/**
 * Set the OIDC authority URL for connectivity verification.
 * Call this once during app initialization.
 */
export function setOidcAuthorityUrl(url: string): void {
  _oidcAuthorityUrl = url;
}

// ============================================================================
// Auth failure detection — lets the auth layer know when API calls get 401
// ============================================================================
let _onAuthFailure: (() => void) | null = null;

/**
 * Register a callback that fires when an API call receives a 401.
 * The auth layer should use this to clear the user and trigger re-login.
 */
export function setOnAuthFailure(callback: (() => void) | null): void {
  _onAuthFailure = callback;
}

async function notifyAuthFailure(): Promise<void> {
  if (!_onAuthFailure) return;

  // Don't redirect to login if the user is actually offline — verify first.
  const online = await verifyOnlineStatus();
  if (online && _onAuthFailure) {
    console.log('[fetch-wrapper] Notifying auth failure (401, confirmed online)');
    _onAuthFailure();
  }
}

/**
 * Get current online state.
 */
export function getIsOnline(): boolean {
  return _isOnline;
}

/**
 * Subscribe to online state changes.
 * Returns an unsubscribe function.
 */
export function subscribeToOnlineState(listener: (online: boolean) => void): () => void {
  console.log('[Online State] New subscriber added, total listeners:', _onlineStateListeners.size + 1);
  _onlineStateListeners.add(listener);
  // Immediately call with current state
  console.log('[Online State] Calling new listener with current state:', _isOnline);
  listener(_isOnline);
  return () => {
    _onlineStateListeners.delete(listener);
    console.log('[Online State] Subscriber removed, total listeners:', _onlineStateListeners.size);
  };
}

/**
 * Update online state and notify listeners.
 */
function setOnlineState(online: boolean): void {
  console.log('[Online State] setOnlineState called with:', online, 'current state:', _isOnline, 'listeners:', _onlineStateListeners.size);
  if (_isOnline !== online) {
    console.log('[Online State] State changing to:', online ? 'online' : 'offline');
    _isOnline = online;
    _onlineStateListeners.forEach(listener => {
      try {
        console.log('[Online State] Notifying listener');
        listener(online);
      } catch (e) {
        console.error('Error in online state listener:', e);
      }
    });
  }
}

/**
 * Verify offline status by pinging the OIDC endpoint.
 * Called when a request fails to confirm it's not a one-off failure.
 * Throttled to run at most once every 5 seconds to avoid spamming the endpoint
 * when multiple requests fail simultaneously.
 */
async function verifyOfflineStatus(): Promise<void> {
  // If we're already offline, no need to verify again
  if (!_isOnline) {
    return;
  }

  // Throttle: don't verify if we've checked recently
  const now = Date.now();
  if (now - _lastVerificationTime < VERIFICATION_THROTTLE_MS) {
    console.log('[Online State] Verification throttled, last check was', now - _lastVerificationTime, 'ms ago');
    return;
  }

  // Prevent concurrent verifications
  if (_verifyingOffline) return;
  _verifyingOffline = true;
  _lastVerificationTime = now;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const verifyUrl = _oidcAuthorityUrl
      ? `${_oidcAuthorityUrl}/.well-known/openid-configuration`
      : 'https://accounts.readcloud.com/.well-known/openid-configuration';

    console.log('[Online State] Verifying connectivity via:', verifyUrl);

    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await adaptiveFetch(verifyUrl, {
      method: 'HEAD',
      signal: controller.signal,
    });

    // OIDC endpoint reachable - we're online
    console.log('[Online State] Verification succeeded, we are online');
    setOnlineState(true);
  } catch (error) {
    // OIDC endpoint also unreachable - we're truly offline
    console.log('[Online State] Verification failed, we are offline:', error);
    setOnlineState(false);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    _verifyingOffline = false;
  }
}

/**
 * Report a network error from external code (e.g., document loading).
 * This triggers the OIDC verification to confirm offline status.
 */
export function reportNetworkError(): void {
  verifyOfflineStatus();
}

/**
 * Manually verify online status by pinging the OIDC endpoint.
 * Unlike verifyOfflineStatus, this is NOT throttled and is intended
 * for user-initiated actions (e.g., clicking a refresh button).
 * Updates the global online state and returns the result.
 */
export async function verifyOnlineStatus(): Promise<boolean> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const verifyUrl = _oidcAuthorityUrl
      ? `${_oidcAuthorityUrl}/.well-known/openid-configuration`
      : 'https://accounts.readcloud.com/.well-known/openid-configuration';

    console.log('[Online State] Manual verification via:', verifyUrl);

    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await adaptiveFetch(verifyUrl, {
      method: 'HEAD',
      signal: controller.signal,
    });

    // OIDC endpoint reachable - we're online
    console.log('[Online State] Manual verification succeeded, we are online');
    setOnlineState(true);
    return true;
  } catch (error) {
    // OIDC endpoint unreachable - we're offline
    console.log('[Online State] Manual verification failed, we are offline:', error);
    setOnlineState(false);
    return false;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * Pull a human-readable message out of an API error body. The v14 endpoints
 * are not consistent: { error }, { message }, { result: { error } } and plain
 * text all occur in the wild.
 */
export function extractApiErrorMessage(body: string): string | null {
  if (!body) return null;
  try {
    const json = JSON.parse(body);
    const message =
      json?.error?.message || json?.error || json?.message || json?.result?.error;
    return typeof message === 'string' && message ? message : null;
  } catch {
    return body.length <= 300 ? body : null;
  }
}

/**
 * Thrown when the server answered with a non-2xx status. Kept distinct from
 * network errors so the offline queue never captures server rejections
 * (e.g. profanity-filter 400s) — those must surface to the caller.
 */
export class HttpError extends Error {
  status: number;
  body: string;

  constructor(status: number, body: string) {
    super(
      extractApiErrorMessage(body) || `Request failed with status ${status}`
    );
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Check if an error looks like a network error.
 */
function isNetworkError(error: unknown): boolean {
  const message = (typeof error === 'string' ? error : (error as Error)?.message || '').toLowerCase();
  return (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('fetch failed') ||
    message.includes('failed to send request') ||
    message.includes('error sending request') || // Tauri/Rust error format
    message.includes('connection') ||
    message.includes('timeout') ||
    message.includes('dns') ||
    message.includes('no such host') ||
    message.includes('unreachable')
  );
}

/**
 * Decodes a base64url-encoded string to standard base64, then decodes it.
 * JWTs use base64url encoding (RFC 7519) which differs from standard base64:
 * - Uses '-' instead of '+'
 * - Uses '_' instead of '/'
 * - Often omits '=' padding
 */
function base64UrlDecode(base64url: string): string {
  // Replace base64url characters with standard base64 equivalents
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if necessary
  const padding = base64.length % 4;
  if (padding) {
    base64 += '='.repeat(4 - padding);
  }

  return atob(base64);
}

function extractUserIdFromAuthHeader(
  headers: Record<string, string> | Headers | undefined
): string | null {
  if (!headers) return null;

  const get = (key: string) => {
    if (headers instanceof Headers) return headers.get(key);
    return (
      (headers as Record<string, string>)[key] ||
      (headers as Record<string, string>)[key.toLowerCase()]
    );
  };

  const authHeader = get('Authorization') || get('authorization');
  if (!authHeader || !authHeader.startsWith('Bearer ')) return null;

  try {
    const token = authHeader.substring(7);
    const payload = JSON.parse(base64UrlDecode(token.split('.')[1]));
    const userId =
      payload.sub || payload.userId || payload.id || payload.user_id;
    return userId && typeof userId === 'string' ? userId : null;
  } catch {
    return null;
  }
}

function parseApiMutation(url: string): {
  action: 'add' | 'update' | 'delete';
  entityType: string;
} | null {
  const match = url.match(/\/api\/v14\/(add|update|delete)\/([^/?#]+)/i);
  if (!match) return null;
  const action = match[1].toLowerCase() as 'add' | 'update' | 'delete';
  const entityType = match[2];
  return { action, entityType };
}

function safeParseJson(body: unknown): any | null {
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (body && typeof body === 'object') return body;
  return null;
}

/**
 * Mirror a server-acknowledged mutation into the local offline cache. Called
 * only after the server accepts (or when queueing while offline) — a rejected
 * mutation must never enter the cache, or the next offline get/all would
 * resurrect it.
 */
async function persistMutationLocally(
  url: string,
  mutation: { action: 'add' | 'update' | 'delete'; entityType: string },
  parsedBody: any | null,
  userId: string | null
): Promise<void> {
  const tableName = `${mutation.entityType}s`;
  try {
    if (mutation.action === 'delete') {
      let entityId: string | null =
        parsedBody && typeof parsedBody.id === 'string' ? parsedBody.id : null;
      if (!entityId) {
        try {
          entityId = new URL(url).searchParams.get('id');
        } catch {
          // ignore
        }
      }
      if (entityId) {
        await deleteItem(tableName, entityId, userId || undefined);
      }
    } else if (
      Array.isArray(parsedBody) &&
      mutation.entityType === 'app_state'
    ) {
      // /update/app_state takes a batch array of {state, tags, date} items
      // keyed by (tags.realm, tags.bookId). Route to the camelCase
      // `appStates_<userId>` table that /get/all writes to and
      // reconstructResponse reads from, so offline open/reopen sees the
      // user's latest local viewstate.
      await upsertAppStateBatch(parsedBody, userId || undefined);
    } else if (parsedBody && !Array.isArray(parsedBody)) {
      await upsertItem(tableName, parsedBody, userId || undefined);
    }
  } catch {
    // best effort — the cache is corrected by the next successful get/all
  }
}

function shouldIntercept(url: string, method: string): boolean {
  const fileExtension = url.split('.').pop()?.toLowerCase();
  const extensions = [
    // Documents
    'pdf',
    'epub',
    // Images
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'svg',
    'bmp',
    'tif',
    'tiff',
    'ico',
    'avif',
    'heic',
    'heif',
    // Video
    'mp4',
    'm4v',
    'webm',
    'ogv',
    'mov',
    'mkv',
    'avi',
    'flv',
    // Audio
    'mp3',
    'm4a',
    'aac',
    'wav',
    'ogg',
    'oga',
    'flac',
    'opus',
  ];

  // Check if URL matches any of our patterns
  const urlPatterns = [
    '*.readcloud.com/**',
    '*.readcloud-test.com/**',
    'localhost:4200/**',
    'localhost:4300/**',
  ];

  const urlMatch = urlPatterns.some((pattern) => {
    const regex = new RegExp(pattern.replace(/\*/g, '.*'));
    return regex.test(url);
  });

  const extensionMatch = extensions.includes(fileExtension);
  const methodMatch = method === 'GET' || method === 'HEAD';

  return urlMatch && extensionMatch && methodMatch;
}

function sanitizeUrlToFilename(url: string): string {
  // Get the part of the URL after the last '/'
  const fileName = url.split('/').pop() || '';
  // Sanitize the fileName by replacing any unwanted characters
  return fileName.replace(/[\/:]/g, '_');
}

/**
 * Check if a file is cached locally in the Tauri app data directory.
 * Returns false if not in Tauri environment.
 */
export async function isFileCached(url: string): Promise<boolean> {
  const isTauriEnv = typeof window !== 'undefined' && '__TAURI__' in window;
  if (!isTauriEnv) return false;

  try {
    const sanitizedFileName = sanitizeUrlToFilename(url);
    const filePath = `readcloud/${sanitizedFileName}`;
    return await exists(filePath, { baseDir: BaseDirectory.AppData });
  } catch {
    return false;
  }
}

/**
 * For a cached book/media file, return both an `asset://` URL and the file
 * size. The URL lets the webview fetch bytes directly via Tauri's asset
 * protocol — no JS IPC, no byte buffer copy, and Range: requests are supported
 * natively. The size is needed for PDFDataRangeTransport.
 *
 * Uses `stat()` as a combined exists-and-size check (one IPC call).
 *
 * Returns null when:
 *  - not running in Tauri (web/dev server build)
 *  - the file isn't cached yet (caller should fall back to the download path)
 *  - any IPC error occurs during the lookup
 */
export async function resolveTauriBookAssetInfo(
  url: string
): Promise<{ assetUrl: string; size: number } | null> {
  const isTauriEnv = typeof window !== 'undefined' && '__TAURI__' in window;
  if (!isTauriEnv) return null;

  const sanitizedFileName = sanitizeUrlToFilename(url);
  const relativePath = `readcloud/${sanitizedFileName}`;

  try {
    const { stat } = await import('@tauri-apps/plugin-fs');
    const info = await stat(relativePath, {
      baseDir: BaseDirectory.AppData,
    });
    if (!info.isFile) return null;

    const dir = await appDataDir();
    const absolutePath = await join(dir, 'readcloud', sanitizedFileName);
    return {
      assetUrl: convertFileSrc(absolutePath, 'asset'),
      size: info.size,
    };
  } catch (error) {
    // stat throws when the file is missing; that's a normal cache miss.
    return null;
  }
}

/**
 * Convenience wrapper around `resolveTauriBookAssetInfo` for callers that
 * only need the URL (e.g. the non-DRM PDF path that hands the URL to PDF.js).
 */
export async function resolveTauriBookAssetUrl(
  url: string
): Promise<string | null> {
  const info = await resolveTauriBookAssetInfo(url);
  return info?.assetUrl ?? null;
}

/**
 * Delete a cached book file from the Tauri app data directory.
 * Returns true if successfully deleted or file didn't exist.
 * Returns false if not in Tauri environment or if deletion fails.
 */
export async function deleteBookCache(url: string): Promise<boolean> {
  const isTauriEnv = typeof window !== 'undefined' && '__TAURI__' in window;
  if (!isTauriEnv) return false;

  try {
    const sanitizedFileName = sanitizeUrlToFilename(url);
    const filePath = `readcloud/${sanitizedFileName}`;

    // Check if file exists first
    const fileExists = await exists(filePath, { baseDir: BaseDirectory.AppData });
    if (!fileExists) return true; // Already deleted

    // Delete the file
    await remove(filePath, { baseDir: BaseDirectory.AppData });

    // Clear from the known existing files cache if it exists
    if ((window as any).__rc_knownExisting) {
      (window as any).__rc_knownExisting.delete(sanitizedFileName);
    }

    return true;
  } catch (error) {
    console.error('Failed to delete cached file:', error);
    return false;
  }
}

// Track in-flight Rust downloads by URL so we can cancel by URL
const getDownloadMap = () => {
  if (!(window as any).__rc_downloadIds) {
    (window as any).__rc_downloadIds = new Map<string, string>();
  }
  return (window as any).__rc_downloadIds as Map<string, string>;
};

export async function cancelTauriDownloadByUrl(url: string): Promise<void> {
  try {
    const map = getDownloadMap();
    const id = map.get(url);
    if (!id) return;
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('cancel_download', { id });
    map.delete(url);
  } catch {}
}

async function handleTauriFetch(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  // Add request counter to detect infinite loops
  const currentRequest = ++requestCount;

  try {
    const sanitizedFileName = sanitizeUrlToFilename(url);
    const filePath = `readcloud/${sanitizedFileName}`;

    // Check if file exists in storage
    const fileExists = await exists(filePath, {
      baseDir: BaseDirectory.AppData,
    });

    const responseHeaders = new Headers({
      ...(options.headers as Record<string, string>),
      'Content-Type': getContentType(url) || 'application/octet-stream',
    });

    // Cache miss: download into memory, hand bytes back immediately, and let Rust
    // persist the file to disk in the background so future opens hit the cache.
    if (!fileExists) {
      const { invoke } = await import('@tauri-apps/api/core');
      const { listen } = await import('@tauri-apps/api/event');

      let unsubscribe: (() => void) | undefined;
      if (options.onProgress) {
        unsubscribe = await listen('download-progress', (event: any) => {
          options.onProgress!(event.payload.percentage);
        });
      }

      const id = `${Date.now()}-${Math.random()}`;
      getDownloadMap().set(url, id);
      options.onDownloadStart?.({ id, url, filename: sanitizedFileName });

      try {
        const bytes = (await invoke('download_file_to_memory', {
          id,
          url,
          filename: sanitizedFileName,
          headers: options.headers as Record<string, string>,
        })) as ArrayBuffer;

        return new Response(bytes, {
          status: 200,
          headers: responseHeaders,
        });
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : String(error);
        if (isNetworkError(errorMsg)) {
          verifyOfflineStatus();
        }
        throw error instanceof Error ? error : new Error(errorMsg);
      } finally {
        if (unsubscribe) unsubscribe();
        getDownloadMap().delete(url);
      }
    }

    // Cache hit: stream bytes off disk.
    return new Response(
      new ReadableStream({
        start(controller) {
          import('@tauri-apps/plugin-fs').then(async ({ readFile }) => {
            try {
              const fileContent = await readFile(filePath, {
                baseDir: BaseDirectory.AppData,
              });
              controller.enqueue(fileContent);
              controller.close();
            } catch (error) {
              console.error(
                'ReadableStream: Error reading file from disk:',
                error
              );
              controller.error(error);
            }
          });
        },
      }),
      {
        status: 200,
        headers: responseHeaders,
      }
    );
  } catch (error) {
    throw error;
  }
}

async function basicTauriRequest(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

  // Handle body: pass FormData as-is, stringify objects, keep strings
  const body =
    options.body instanceof FormData
      ? options.body
      : typeof options.body === 'string'
      ? options.body
      : options.body
      ? JSON.stringify(options.body)
      : undefined;

  if (isTauri) {
    try {
      const response = await adaptiveFetch(url, {
        ...options,
        body,
      });

      if (!response.ok) {
        if (response.status === 401) {
          notifyAuthFailure();
        }
        const errorBody = await response.text().catch(() => '');
        throw new HttpError(response.status, errorBody);
      }

      return response;
    } catch (error) {
      throw error;
    }
  } else {
    const response = await window.fetch(url, {
      ...options,
      body,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new HttpError(response.status, errorBody);
    }

    return response;
  }
}

// Dedup concurrent /get/all calls by URL + body — two callers requesting the
// same collections in the same instant share one network round-trip and one
// cache-save pass. Response.clone() gives each caller its own readable body.
const _inFlightGetAll = new Map<string, Promise<Response>>();

function getAllDedupKey(url: string, options: GetAllFetchOptions): string {
  const body =
    typeof options.body === 'string'
      ? options.body
      : options.body
      ? JSON.stringify(options.body)
      : '';
  return `${(options.method || 'POST').toUpperCase()}:${url}:${body}`;
}

async function getAllTauriRequest(
  url: string,
  options: GetAllFetchOptions = {}
): Promise<Response> {
  const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

  if (isTauri) {
    const dedupKey = getAllDedupKey(url, options);
    const existing = _inFlightGetAll.get(dedupKey);
    if (existing) {
      console.log('[getAllTauriRequest] Deduping to in-flight request:', url);
      return existing.then((r) => r.clone());
    }

    const promise = _performGetAllTauriRequest(url, options);
    _inFlightGetAll.set(dedupKey, promise);
    promise.finally(() => {
      _inFlightGetAll.delete(dedupKey);
    });
    return promise.then((r) => r.clone());
  } else {
    const response = await window.fetch(url, {
      ...options,
      body:
        options.body instanceof FormData
          ? options.body
          : typeof options.body === 'string'
          ? options.body
          : options.body
          ? JSON.stringify(options.body)
          : undefined,
    });

    if (!response.ok) {
      throw new Error(`Fetch failed with status: ${response.status}`);
    }

    return response;
  }
}

async function _performGetAllTauriRequest(
  url: string,
  options: GetAllFetchOptions
): Promise<Response> {
  // Extract user ID from Authorization header
  const userId = extractUserIdFromAuthHeader(
    options.headers as Record<string, string> | undefined
  );

  try {
      // Handle body: pass FormData as-is, stringify objects, keep strings
      const body =
        options.body instanceof FormData
          ? options.body
          : typeof options.body === 'string'
          ? options.body
          : options.body
          ? JSON.stringify(options.body)
          : undefined;

      const response = await adaptiveFetch(url, {
        ...options,
        body,
      });

      if (response.status === 200) {
        // Guard against CloudFront returning index.html with a 200
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          console.warn(
            '[getAllTauriRequest] Non-JSON content-type on 200:',
            contentType
          );
          throw new Error('Received non-JSON 200 response (likely CloudFront fallback)');
        }

        const jsonData = await response.json();

        // Guard against 200 responses that carry an error payload —
        // the server (or CloudFront) can return { error: "..." } or
        // { result: { error: "..." } } with a 200 status.
        const hasError =
          jsonData.error ||
          jsonData.errors?.length ||
          jsonData.result?.error;
        if (hasError) {
          console.warn(
            '[getAllTauriRequest] 200 response contains error:',
            jsonData.error || jsonData.result?.error || jsonData.errors
          );
          throw new Error('Server returned an error in 200 response');
        }

        // We reached the server with a valid response - we're online
        setOnlineState(true);
        // Try flushing any queued offline mutations in the background.
        processPendingChanges({ userId, limit: 50 }).catch(() => {});

        // Parse the body if it's a string to check for get: true
        const parsedBody =
          typeof options.body === 'string'
            ? (JSON.parse(options.body) as GetAllRequestBody)
            : options.body;

        // Save collections that were requested with "get: true".
        // Cache saves are best-effort — a failure must never cause the
        // valid server response to be discarded in favour of stale cache.
        if (parsedBody) {
          for (const [key, value] of Object.entries(parsedBody)) {
            if (
              value &&
              typeof value === 'object' &&
              'get' in value &&
              value.get === true &&
              jsonData.result[key.replace('Opts', 's')]
            ) {
              const tableName = key.replace('Opts', 's');

              try {
                await saveCollection(
                  tableName,
                  jsonData.result[tableName],
                  userId
                );
              } catch (cacheError) {
                console.warn(
                  `[getAllTauriRequest] Failed to save collection "${tableName}" to cache:`,
                  cacheError
                );
              }
            }
          }
        }

        return new Response(JSON.stringify(jsonData), {
          status: response.status,
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        // Non-200 response - likely offline or server error
        console.log('[getAllTauriRequest] Non-200 response:', response.status);

        // 401 means the access token is expired/invalid — signal auth layer
        if (response.status === 401) {
          notifyAuthFailure();
        }

        // Trigger offline verification since we couldn't get a successful response
        verifyOfflineStatus();

        const parsedBody =
          typeof options.body === 'string'
            ? (JSON.parse(options.body) as GetAllRequestBody)
            : options.body;

        const cachedResponse = await reconstructResponse(parsedBody, userId);
        if (cachedResponse) {
          console.log('[getAllTauriRequest] Returning cached response');
          return new Response(JSON.stringify(cachedResponse), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }

        throw new Error(
          `Request failed with status ${response.status} and no cached data found.`
        );
      }
    } catch (error) {
      console.log('[getAllTauriRequest] Caught error:', error);
      console.log('[getAllTauriRequest] isNetworkError:', isNetworkError(error));

      // Verify offline status when network request fails
      if (isNetworkError(error)) {
        console.log('[getAllTauriRequest] Triggering verifyOfflineStatus');
        verifyOfflineStatus();
      }

      const parsedBody =
        typeof options.body === 'string'
          ? (JSON.parse(options.body) as GetAllRequestBody)
          : options.body;

      const cachedResponse = await reconstructResponse(
        parsedBody || {},
        userId
      );
      if (cachedResponse) {
        console.log('[getAllTauriRequest] Returning cached response after error');
        return new Response(JSON.stringify(cachedResponse), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }

    throw new Error('Request failed and no cached data found.');
  }
}

function getContentType(url: string): string | null {
  const extension = url.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'css':
      return 'text/css';
    case 'js':
      return 'application/javascript';
    case 'html':
      return 'text/html';
    case 'json':
      return 'application/json';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'gif':
      return 'image/gif';
    case 'bmp':
      return 'image/bmp';
    case 'tif':
    case 'tiff':
      return 'image/tiff';
    case 'svg':
      return 'image/svg+xml';
    case 'webp':
      return 'image/webp';
    case 'ico':
      return 'image/x-icon';
    case 'avif':
      return 'image/avif';
    case 'heic':
      return 'image/heic';
    case 'heif':
      return 'image/heif';
    case 'pdf':
      return 'application/pdf';
    case 'epub':
      return 'application/epub+zip';
    // Video
    case 'mp4':
      return 'video/mp4';
    case 'm4v':
      return 'video/x-m4v';
    case 'webm':
      return 'video/webm';
    case 'ogv':
      return 'video/ogg';
    case 'mov':
      return 'video/quicktime';
    case 'mkv':
      return 'video/x-matroska';
    case 'avi':
      return 'video/x-msvideo';
    case 'flv':
      return 'video/x-flv';
    // Audio
    case 'mp3':
      return 'audio/mpeg';
    case 'm4a':
      return 'audio/mp4';
    case 'aac':
      return 'audio/aac';
    case 'wav':
      return 'audio/wav';
    case 'ogg':
    case 'oga':
      return 'audio/ogg';
    case 'flac':
      return 'audio/flac';
    case 'opus':
      return 'audio/opus';
    default:
      return null;
  }
}

export async function wrappedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url = input instanceof Request ? input.url : input.toString();
  const options = init || {};
  const isTauri = !!(window as any).__TAURI__;
  const shouldUseTauri = (options as FetchOptions).useTauri ?? isTauri;
  const method = options.method || 'GET';

  // Convert Headers object to plain object if present
  const processedOptions = {
    ...options,
    headers:
      options.headers instanceof Headers
        ? (() => {
            const headers: Record<string, string> = {};
            (options.headers as Headers).forEach((value, key) => {
              headers[key] = value;
            });
            return headers;
          })()
        : options.headers,
  };

  // If we're not in Tauri environment or shouldn't use Tauri, use regular fetch
  if (!shouldUseTauri) {
    return window.fetch(input, init);
  }

  // Check if this request should be intercepted
  if (shouldIntercept(url, method)) {
    return handleTauriFetch(url, processedOptions as FetchOptions);
  }

  // Check if this is a get/all request by looking for "/get/all" in the URL
  if (url.includes('/get/all')) {
    return getAllTauriRequest(url, processedOptions as GetAllFetchOptions);
  }

  // For non-intercepted and non-get/all requests, use basic Tauri request
  const methodUpper = (method || 'GET').toString().toUpperCase();
  const mutation = parseApiMutation(url);
  const isMutationMethod = ['POST', 'PUT', 'DELETE'].includes(methodUpper);
  const userId = extractUserIdFromAuthHeader(processedOptions.headers as any);

  const mutationBody =
    mutation && isMutationMethod
      ? safeParseJson((processedOptions as any).body)
      : null;

  try {
    const response = await basicTauriRequest(url, processedOptions as FetchOptions);
    // Successful response means we're online
    setOnlineState(true);
    if (mutation && isMutationMethod) {
      // Mirror the acknowledged mutation into the offline cache. This runs
      // after the server accepts — a rejected mutation (e.g. profanity-filter
      // 400) must never be persisted locally.
      await persistMutationLocally(url, mutation, mutationBody, userId);
      processPendingChanges({ userId, limit: 50 }).catch(() => {});
    }
    return response;
  } catch (e) {
    // The server answered — this is a rejection, not an offline event. Don't
    // queue it, don't fabricate success: let the caller reject and revert.
    if (e instanceof HttpError) {
      setOnlineState(true);
      throw e;
    }

    console.log('[wrappedFetch] Caught error:', e);
    console.log('[wrappedFetch] isNetworkError:', isNetworkError(e));

    // If this looks like a network error, verify with OIDC endpoint
    if (isNetworkError(e)) {
      console.log('[wrappedFetch] Triggering verifyOfflineStatus');
      verifyOfflineStatus();
    }

    if (mutation && isMutationMethod) {
      const parsedBody = mutationBody;
      const isBatchBody = Array.isArray(parsedBody);

      // For 'add' mutations, generate a temporary ID if none exists
      // This ensures offline-created entities have a valid ID until synced
      let entityId =
        parsedBody && !isBatchBody && typeof parsedBody.id === 'string'
          ? parsedBody.id
          : null;

      if (mutation.action === 'add' && !isBatchBody && !entityId) {
        entityId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      }

      // Update the body with the temp ID for local storage. Skip this for
      // batch-array bodies — spreading an array into `{...arr, id}` yields
      // `{0: ..., 1: ..., id: null}`, which the server would reject when the
      // queued mutation is retried. Batch bodies replay verbatim.
      const bodyWithId =
        parsedBody && !isBatchBody ? { ...parsedBody, id: entityId } : null;
      const rawBody = isBatchBody
        ? JSON.stringify(parsedBody)
        : bodyWithId
        ? JSON.stringify(bodyWithId)
        : null;

      await enqueuePendingChange({
        userId,
        url,
        method: methodUpper,
        headers: processedOptions.headers as any,
        body: rawBody,
        entityType: mutation.entityType,
        entityId,
        action: mutation.action,
      });

      // Mirror the queued mutation into the local cache (with the temp ID
      // for adds) so offline get/all reconstruction reflects it.
      await persistMutationLocally(
        url,
        mutation,
        isBatchBody ? parsedBody : bodyWithId ?? parsedBody,
        userId
      );

      // Return a response that mimics the server response structure
      // so thunks can extract the ID properly
      return new Response(JSON.stringify({
        queued: true,
        result: { id: entityId }
      }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    throw e;
  }
}

// Utility function to ensure we're using our wrapped fetch
export function ensureWrappedFetch(): typeof window.fetch {
  const isTauri = !!(window as any).__TAURI__;
  if (isTauri) {
    return wrappedFetch;
  }
  return window.fetch;
}

// Export the wrapped fetch with a more specific name
export const tauriFetch = wrappedFetch;

// Export a function that can be used to patch fetch in third-party code
export function patchFetch(target: any, propertyKey: string = 'fetch'): void {
  if (target && typeof target[propertyKey] === 'function') {
    const originalFetch = target[propertyKey];
    target[propertyKey] = function (
      input: RequestInfo | URL,
      init?: RequestInit
    ) {
      return ensureWrappedFetch()(input, init);
    };
  }
}

// Module-level cache of fully-resolved src → final URL (post convertFileSrc).
// Populated on first successful resolution; lets subsequent mounts return the
// URL synchronously without any IPC (no appDataDir, no join, no semaphore).
function getResolvedUrlCache(): Map<string, string> {
  if (!(window as any).__rc_resolvedMediaUrls) {
    (window as any).__rc_resolvedMediaUrls = new Map<string, string>();
  }
  return (window as any).__rc_resolvedMediaUrls as Map<string, string>;
}

// Synchronous lookup — returns the previously-resolved URL for `src`, or null.
// Components can use this to set their initial state without flashing a
// loading skeleton on remount when the thumbnail is already cached.
export function getCachedTauriMediaUrl(src: string): string | null {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return null;
  return getResolvedUrlCache().get(src) ?? null;
}

// Resolve a media URL to a disk-served URL when possible; fall back to a Blob URL
// Note: no global cache; callers should revoke blob: URLs when unmounted if desired
export async function resolveTauriMediaUrl(
  src: string,
  mimeType?: string
): Promise<string> {
  // Fast path: already resolved before. Skip all IPC + the semaphore.
  const alreadyResolved = getResolvedUrlCache().get(src);
  if (alreadyResolved) return alreadyResolved;

  console.log('resolveTauriMediaUrl', src, mimeType);

  // Concurrency guards and memoization
  // Deduplicate concurrent resolutions of the same src
  if (!(window as any).__rc_pendingResolutions) {
    (window as any).__rc_pendingResolutions = new Map<
      string,
      Promise<string>
    >();
  }
  const pendingResolutions = (window as any).__rc_pendingResolutions as Map<
    string,
    Promise<string>
  >;

  // Global resolver-level semaphore to limit the number of concurrent resolves
  if (!(window as any).__rc_resolveSemaphore) {
    (window as any).__rc_resolveSemaphore = {
      queue: [] as Array<() => void>,
      inFlight: 0,
      max: 8,
    };
  }
  const resolveSem = (window as any).__rc_resolveSemaphore as {
    queue: Array<() => void>;
    inFlight: number;
    max: number;
  };
  const resolveRun = async <T>(fn: () => Promise<T>): Promise<T> => {
    if (resolveSem.inFlight < resolveSem.max) {
      resolveSem.inFlight++;
      try {
        return await fn();
      } finally {
        resolveSem.inFlight--;
        const next = resolveSem.queue.shift();
        if (next) next();
      }
    }
    return new Promise<T>((resolve, reject) => {
      resolveSem.queue.push(async () => {
        resolveSem.inFlight++;
        try {
          const result = await fn();
          resolve(result);
        } catch (e) {
          reject(e);
        } finally {
          resolveSem.inFlight--;
          const next = resolveSem.queue.shift();
          if (next) next();
        }
      });
    });
  };

  // Cache app data dir path to reduce IPC calls
  if (!(window as any).__rc_appDataDirPath) {
    (window as any).__rc_appDataDirPath = appDataDir();
  }
  const appDataDirPathPromise = (window as any)
    .__rc_appDataDirPath as Promise<string>;

  // Remember which sanitized filenames were confirmed on disk
  if (!(window as any).__rc_knownExisting) {
    (window as any).__rc_knownExisting = new Set<string>();
  }
  const knownExisting = (window as any).__rc_knownExisting as Set<string>;

  const existingPending = pendingResolutions.get(src);
  if (existingPending) return existingPending;

  const resolvedUrlCache = getResolvedUrlCache();

  const resolveFromDisk = async (sanitized: string): Promise<string> => {
    const dir = await appDataDirPathPromise;
    const filePath = await join(dir, 'readcloud', sanitized);
    const url = convertFileSrc(filePath, 'asset');
    resolvedUrlCache.set(src, url);
    return url;
  };

  const sanitizedEarly = sanitizeUrlToFilename(src);
  // Cache-hit fast path — skip the semaphore entirely. The semaphore exists
  // to limit concurrent *downloads*; serving a file we've already confirmed
  // on disk shouldn't queue behind in-flight book downloads.
  if (knownExisting.has(sanitizedEarly)) {
    const promise = resolveFromDisk(sanitizedEarly);
    pendingResolutions.set(src, promise);
    try {
      return await promise;
    } finally {
      pendingResolutions.delete(src);
    }
  }

  const promise = resolveRun(async () => {
    // Prefer serving from disk when available
    const sanitized = sanitizeUrlToFilename(src);
    if (knownExisting.has(sanitized)) {
      return resolveFromDisk(sanitized);
    }

    const fileExists = await exists(`readcloud/${sanitized}`, {
      baseDir: BaseDirectory.AppData,
    });
    if (fileExists) {
      console.log('fileExists', fileExists);
      knownExisting.add(sanitized);
      return resolveFromDisk(sanitized);
    }

    // Not on disk yet: invoke Rust download_file directly to avoid redundant IPC
    // (bypasses handleTauriFetch which does its own exists() + readFile() unnecessarily)
    try {
      console.log('downloading', src);
      const { invoke } = await import('@tauri-apps/api/core');
      const downloadId = `${Date.now()}-${Math.random()}`;
      const downloadResult = (await invoke('download_file', {
        id: downloadId,
        url: src,
        filename: sanitized,
        headers: { accept: '*/*' },
      })) as { success: boolean; file_path?: string; error?: string };

      if (downloadResult.success) {
        knownExisting.add(sanitized);
        return resolveFromDisk(sanitized);
      }

      // Download reported failure — fall back to HTTP plugin fetch + blob URL
      console.log('download_file failed:', downloadResult.error);
    } catch (e) {
      console.log('download_file error:', e);
    }

    // Fallback: fetch via HTTP plugin and return ephemeral blob URL
    try {
      const response = await adaptiveFetch(src, {
        method: 'GET',
        headers: { accept: '*/*' },
      });
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], {
        type: mimeType || 'application/octet-stream',
      });
      return URL.createObjectURL(blob);
    } catch {
      console.log('fallback fetch also failed for', src);
      return src; // last resort: return original URL
    }
  });

  pendingResolutions.set(src, promise);
  try {
    return await promise;
  } finally {
    pendingResolutions.delete(src);
  }
}

/**
 * Check if the device is online.
 * Returns the current known online state, which is updated based on actual API requests.
 */
export function checkIsOnlineTauri(): boolean {
  return getIsOnline();
}
